import { externalConstraint } from '@flatfile/plugin-constraints'
import { recordHook } from '@flatfile/plugin-record-hook'
import {
  formatPlanEin,
  formatPlanName,
  formatPlanNumber,
  formatZipCode,
  formatSsn,
  formatSearchPlanSponsorName,
  formatSearchDob,
  formatSearchDod,
  formatSearchState,
  validateSearchSsn,
  validateSearchDob,
  validateSearchDod,
  validateSearchZipCode,
  validateSearchAddressSsnCrossField,
} from './participantsDataFormatting'
import { buildSearchValidationContext } from './searchValidationContext'

export const searchServiceListeners = (listener) => {
  const ctx = buildSearchValidationContext()

  // Formatting: single hook runs all field formatters in sequence
  listener.use(
    recordHook('search-record', (record) => {
      formatSearchPlanSponsorName(record)
      formatPlanName(record)
      formatPlanEin(record)
      formatPlanNumber(record)
      formatSsn(record)
      formatSearchState(record)
      formatZipCode(record)
      formatSearchDob(record)
      formatSearchDod(record)
      return record
    })
  )

  // Validation: single hook runs all validators in sequence
  listener.use(
    recordHook('search-record', (record) => {
      validateSearchSsn(record, ctx)
      validateSearchDob(record, ctx)
      validateSearchDod(record)
      validateSearchZipCode(record)
      validateSearchAddressSsnCrossField(record)
      return record
    })
  )

  listener.use(
    externalConstraint('planSponsorName', (value, key, { record }) => {
      const rules = ctx.planSponsorName
      if (value && rules.invalidString.re.test(value))
        record.addError(key, rules.invalidString.error)
      if (value && rules.prohibitedCompanies.re.test(value))
        record.addError(key, rules.prohibitedCompanies.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('planName', (value, key, { record }) => {
      const { emptyValue } = ctx.planName
      if (value && emptyValue.re.test(value))
        record.addError(key, emptyValue.error)
    })
  )

  listener.use(
    externalConstraint('planEin', (value, key, { record }) => {
      const rules = ctx.planEin
      if (value && !rules.invalidEINFormat.re.test(value))
        record.addError(key, rules.invalidEINFormat.error)
      if (value && !rules.invalidEIN.re.test(value))
        record.addError(key, rules.invalidEIN.error)
    })
  )

  listener.use(
    externalConstraint('planNumber', (value, key, { record }) => {
      const rules = ctx.planNumber
      if (value && rules.invalidChars.re.test(value))
        record.addError(key, rules.invalidChars.error)
      if (value && !rules.mustBeThreeDigits.re.test(value))
        record.addError(key, rules.mustBeThreeDigits.error)
    })
  )

  listener.use(
    externalConstraint('ssn', (value, key, { record }) => {
      const { validityCheck } = ctx.ssn
      if (value && validityCheck.re.test(value))
        record.addError(key, validityCheck.error)
    })
  )

  listener.use(
    externalConstraint('firstName', (value, key, { record }) => {
      const rules = ctx.firstName
      if (value && rules.namePrefix.re.test(value))
        record.addError(key, rules.namePrefix.error)
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
      if (value && rules.allowedNameCharacters.re.test(value))
        record.addError(key, rules.allowedNameCharacters.error)
    })
  )

  listener.use(
    externalConstraint('middleName', (value, key, { record }) => {
      const rules = ctx.middleName
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.allowedNameCharacters.re.test(value))
        record.addError(key, rules.allowedNameCharacters.error)
    })
  )

  listener.use(
    externalConstraint('lastName', (value, key, { record }) => {
      const rules = ctx.lastName
      if (value && rules.notApplicable.re.test(value))
        record.addError(key, rules.notApplicable.error)
      if (value && rules.maxChar.re.test(value))
        record.addError(key, rules.maxChar.error)
      if (value && rules.emptyValue.re.test(value))
        record.addError(key, rules.emptyValue.error)
      if (value && rules.allowedNameCharacters.re.test(value))
        record.addError(key, rules.allowedNameCharacters.error)
    })
  )

  listener.use(
    externalConstraint('address1', (value, key, { record }) => {
      const rules = ctx.address1
      if (value && !rules.mustContainNumberAndLetter.re.test(value))
        record.addError(key, rules.mustContainNumberAndLetter.error)
    })
  )

  listener.use(
    externalConstraint('phone', (value, key, { record }) => {
      const { phoneNumber } = ctx.phone
      if (value && !phoneNumber.re.test(value))
        record.addError(key, phoneNumber.error)
    })
  )
}
